/**
 * Trends — a metric over time, with doses marked, and a before/after window.
 *
 * The question this screen serves is "is this doing anything". The honest
 * answer available from one person's log is "here is what the numbers did";
 * the screen shows that well and stops. Rolling means because dailies are
 * noisy; dose events on the same axis so timing can be read directly; plain
 * averages over two windows with the day counts they rest on. No trend lines
 * fitted, no significance, no verdicts.
 */
import { useMemo, useState } from 'preact/hooks';
import type { JSX } from 'preact';
import { Card, Empty, fmt } from '../ui/bits';
import { checkinsRange, compounds, metricsRange, protocols, recentDoses, settings, today } from '../store';
import { MIN_DAYS_PER_SIDE, bounds, compareAround, denseSeries, firstDoseDay, pathFor, rollingMean, type Point } from '../domain/trends';
import { valueOf } from '../data/biometrics';
import { addDays, fromDay } from '../lib/time';
import type { CheckIn, DailyMetrics } from '../data/schema';

const KG_TO_LB = 2.20462262185;

interface MetricDef {
  key: string;
  label: string;
  unit: string;
  dp: number;
  read: (m: DailyMetrics | undefined, c: CheckIn | undefined, weightUnit: 'kg' | 'lb') => number | undefined;
}

const METRICS: MetricDef[] = [
  { key: 'weight', label: 'Weight', unit: '', dp: 1, read: (m, _c, u) => { const kg = valueOf(m, 'weightKg'); return kg === undefined ? undefined : u === 'lb' ? kg * KG_TO_LB : kg; } },
  { key: 'gi', label: 'GI', unit: '/10', dp: 1, read: (_m, c) => c?.gi },
  { key: 'sleep', label: 'Sleep', unit: 'h', dp: 1, read: (m) => { const s = valueOf(m, 'totalSleepSec'); return s === undefined ? undefined : s / 3600; } },
  { key: 'hrv', label: 'HRV', unit: 'ms', dp: 0, read: (m) => valueOf(m, 'hrvMs') },
  { key: 'rhr', label: 'RHR', unit: 'bpm', dp: 0, read: (m) => valueOf(m, 'restingHr') },
  { key: 'energy', label: 'Energy', unit: '/10', dp: 1, read: (_m, c) => c?.energy },
  { key: 'readiness', label: 'Readiness', unit: '', dp: 0, read: (m) => valueOf(m, 'readinessScore') },
];

const RANGES = [30, 90, 180];
const WINDOWS = [14, 30, 60];
const SMOOTH = 7;

export function Trends(): JSX.Element {
  const [metricKey, setMetricKey] = useState('weight');
  const [range, setRange] = useState(90);
  const [windowDays, setWindowDays] = useState(30);
  const comps = compounds.value;
  const [compareId, setCompareId] = useState<string>(comps[0]?.id ?? '');

  const weightUnit = settings.value.weightUnit;
  const metric = METRICS.find((m) => m.key === metricKey) ?? METRICS[0]!;
  const unit = metric.key === 'weight' ? weightUnit : metric.unit;
  const end = today.value;
  const start = addDays(end, -(range - 1));

  const raw = useMemo(() => {
    const byDay = new Map(metricsRange.value.map((m) => [m.day, m]));
    const cByDay = new Map(checkinsRange.value.map((c) => [c.day, c]));
    const rows: { day: string; value: number | undefined }[] = [];
    for (let i = 0; i < range; i++) {
      const day = addDays(start, i);
      rows.push({ day, value: metric.read(byDay.get(day), cByDay.get(day), weightUnit) });
    }
    return denseSeries(rows, start, end);
  }, [metricKey, range, metricsRange.value, checkinsRange.value, weightUnit]);

  const smooth = useMemo(() => rollingMean(raw, SMOOTH, 2), [raw]);
  const doses = recentDoses.value.filter((d) => !d.skipped);
  const marks = doses.filter((d) => d.day >= start && d.day <= end).map((d) => ({ day: d.day, compoundId: d.compoundId }));
  // Anchor: the protocol's explicit "started on" date when the user set one,
  // else the first logged dose. The explicit date matters most for a compound
  // begun before this app existed.
  const starts = comps
    .map((c) => {
      const explicit = protocols.value.find((p) => p.compoundId === c.id && p.startedOn)?.startedOn ?? null;
      const day = explicit ?? firstDoseDay(doses.map((d) => ({ day: d.day, compoundId: d.compoundId })), c.id);
      return { compound: c, day, explicit: explicit !== null };
    })
    .filter((s): s is { compound: typeof comps[number]; day: string; explicit: boolean } => s.day !== null);

  // The comparison uses the full loaded history, not just the visible range,
  // so a 60-day window before a start that is 100 days ago still has data.
  const fullRaw = useMemo(() => {
    const byDay = new Map(metricsRange.value.map((m) => [m.day, m]));
    const cByDay = new Map(checkinsRange.value.map((c) => [c.day, c]));
    const from = addDays(end, -182);
    const rows: { day: string; value: number | undefined }[] = [];
    for (let i = 0; i <= 182; i++) {
      const day = addDays(from, i);
      rows.push({ day, value: metric.read(byDay.get(day), cByDay.get(day), weightUnit) });
    }
    return denseSeries(rows, from, end);
  }, [metricKey, metricsRange.value, checkinsRange.value, weightUnit]);

  const compareStart = starts.find((s) => s.compound.id === compareId)?.day ?? null;
  const comparison = compareStart ? compareAround(fullRaw, compareStart, windowDays) : null;
  const realCount = raw.filter((p) => p.value !== null).length;

  return (
    <>
      <div class="row wrap" style={{ gap: '6px' }}>
        {METRICS.map((m) => (
          <button key={m.key} class="chip" aria-pressed={m.key === metricKey} onClick={() => setMetricKey(m.key)}>{m.label}</button>
        ))}
      </div>

      <Card
        title={`${metric.label} · ${SMOOTH}-day average`}
        action={
          <span class="row" style={{ gap: '4px' }}>
            {RANGES.map((r) => (
              <button key={r} class="chip" style={{ minHeight: '26px', padding: '0 8px' }} aria-pressed={r === range} onClick={() => setRange(r)}>{r}d</button>
            ))}
          </span>
        }
      >
        {realCount < 2 ? (
          <Empty>
            {metric.key === 'gi' || metric.key === 'energy'
              ? 'Nothing to draw yet. Rate this in the daily check-in and it builds up here.'
              : 'Nothing to draw yet. This fills in as syncs and entries arrive.'}
          </Empty>
        ) : (
          <>
            <Chart raw={raw} smooth={smooth} marks={marks} starts={starts.map((s) => ({ day: s.day, color: s.compound.color ?? 'var(--sage)', label: s.compound.name }))} unit={unit} dp={metric.dp} />
            <div class="row wrap" style={{ gap: '10px', marginTop: '8px' }}>
              <span class="row" style={{ gap: '5px' }}><span style={{ width: 14, height: 2, background: 'var(--coral)' }} /><span class="label">{SMOOTH}-day mean</span></span>
              <span class="row" style={{ gap: '5px' }}><span class="dot" style={{ background: 'var(--cream-dim)' }} /><span class="label">daily</span></span>
              {comps.map((c) => (
                <span key={c.id} class="row" style={{ gap: '5px' }}>
                  <span style={{ width: 2, height: 10, background: c.color ?? 'var(--sage)' }} />
                  <span class="label">{c.name} dose</span>
                </span>
              ))}
            </div>
            <p class="note" style={{ marginTop: '8px' }}>{realCount} of {range} days have a value.</p>
          </>
        )}
      </Card>

      <Card title="Before and after">
        {!starts.length ? (
          <Empty>Once a compound has a first logged dose, its start date anchors a before/after window here.</Empty>
        ) : (
          <div class="stack">
            <div class="row wrap" style={{ gap: '6px' }}>
              {starts.map((s) => (
                <button key={s.compound.id} class="chip" aria-pressed={s.compound.id === compareId} onClick={() => setCompareId(s.compound.id)}>
                  <span style={{ width: 8, height: 8, borderRadius: 2, background: s.compound.color ?? 'var(--sage)' }} />
                  {s.compound.name}
                  <span class="dim">{fromDay(s.day).toLocaleDateString(undefined, { month: 'short', day: 'numeric' })}{s.explicit ? '' : ' · first dose'}</span>
                </button>
              ))}
            </div>
            <div class="row" style={{ gap: '6px' }}>
              <span class="label" style={{ marginRight: '4px' }}>Window</span>
              {WINDOWS.map((w) => (
                <button key={w} class="chip" style={{ minHeight: '28px' }} aria-pressed={w === windowDays} onClick={() => setWindowDays(w)}>{w}d</button>
              ))}
            </div>

            {comparison ? <ComparisonBlock c={comparison} metric={metric} unit={unit} windowDays={windowDays} /> : null}

            <p class="note">
              Plain averages of the days that have a value, {windowDays} days either side of the start
              date (the compound's "started on" date when set, otherwise its first logged dose). This is one person's log, with everything else in life changing at the same
              time — a difference here is a reason to look closer, not a finding.
            </p>
          </div>
        )}
      </Card>
    </>
  );
}

function ComparisonBlock(props: { c: ReturnType<typeof compareAround>; metric: MetricDef; unit: string; windowDays: number }): JSX.Element {
  const { c, metric, unit, windowDays } = props;
  const cell = (label: string, w: typeof c.before) => (
    <div class="col" style={{ gap: '4px' }}>
      <span class="label">{label}</span>
      {c.enough && w.mean !== null ? (
        <span class="readout readout-lg">{fmt(w.mean, metric.dp)}<span class="unit"> {unit}</span></span>
      ) : (
        <span class="readout readout-lg" style={{ color: 'var(--muted)' }}>—</span>
      )}
      <span class="label">{w.n}/{windowDays} days</span>
    </div>
  );
  return (
    <div class="card" style={{ background: 'var(--ink-sink)' }}>
      <div class="row-between" style={{ alignItems: 'flex-start' }}>
        {cell('Before', c.before)}
        {cell('After', c.after)}
        <div class="col" style={{ gap: '4px', alignItems: 'flex-end' }}>
          <span class="label">Difference</span>
          {c.enough && c.delta !== null ? (
            <span class="readout readout-lg">{c.delta > 0 ? '+' : ''}{fmt(c.delta, metric.dp)}</span>
          ) : (
            <span class="readout readout-lg" style={{ color: 'var(--muted)' }}>—</span>
          )}
          <span class="label">{c.enough ? `${fmt(c.before.min, metric.dp)}–${fmt(c.before.max, metric.dp)} → ${fmt(c.after.min, metric.dp)}–${fmt(c.after.max, metric.dp)}` : ''}</span>
        </div>
      </div>
      {!c.enough ? (
        <p class="note note-warn" style={{ marginTop: '10px' }}>
          Needs at least {MIN_DAYS_PER_SIDE} days with a value on each side before averages are shown.
        </p>
      ) : null}
    </div>
  );
}

/**
 * The chart. Fixed viewBox so text never distorts; the phone gets ~340 px of
 * width, which the 360-unit box maps to nearly 1:1.
 */
function Chart(props: {
  raw: Point[]; smooth: Point[];
  marks: { day: string; compoundId: string }[];
  starts: { day: string; color: string; label: string }[];
  unit: string; dp: number;
}): JSX.Element {
  const W = 360; const H = 184;
  const padL = 36; const padR = 8; const padT = 24; const padB = 30;
  const n = props.raw.length;
  const { lo, hi } = bounds(props.raw);
  const x = (i: number) => padL + (n > 1 ? (i / (n - 1)) * (W - padL - padR) : 0);
  const y = (v: number) => padT + (1 - (v - lo) / (hi - lo)) * (H - padT - padB);
  const idx = new Map(props.raw.map((p, i) => [p.day, i]));
  const compColor = new Map(compounds.value.map((c) => [c.id, c.color ?? 'var(--sage)']));
  const first = props.raw[0]?.day; const last = props.raw[n - 1]?.day;
  const short = (d: string | undefined) => (d ? fromDay(d).toLocaleDateString(undefined, { month: 'short', day: 'numeric' }) : '');

  return (
    <svg viewBox={`0 0 ${W} ${H}`} style={{ width: '100%', height: 'auto', display: 'block' }} role="img" aria-label="Metric over time with doses marked">
      {/* Gridlines at the axis bounds and midpoint: three, no more. */}
      {[lo, (lo + hi) / 2, hi].map((v, i) => (
        <g key={i}>
          <line x1={padL} x2={W - padR} y1={y(v)} y2={y(v)} stroke="var(--line)" stroke-width="1" />
          <text x={padL - 5} y={y(v) + 3} text-anchor="end" fill="var(--muted)" font-family="var(--mono)" font-size="9">{fmt(v, props.dp)}</text>
        </g>
      ))}
      {/* Compound starts: a dashed rule the full height, labelled at the top. */}
      {props.starts.map((s, k) => {
        const i = idx.get(s.day);
        if (i === undefined) return null;
        // Two compounds means two labels; stagger them onto two rows so they
        // cannot collide, and read right-edge labels leftwards so they never clip.
        const labelY = k % 2 === 0 ? padT - 5 : padT - 14;
        const rightish = x(i) > W * 0.6;
        return (
          <g key={s.label}>
            <line x1={x(i)} x2={x(i)} y1={labelY + 2} y2={H - padB} stroke={s.color} stroke-width="1" stroke-dasharray="3 3" />
            <text x={rightish ? x(i) - 3 : x(i) + 3} y={labelY} text-anchor={rightish ? 'end' : 'start'} fill={s.color} font-family="var(--mono)" font-size="8">{s.label.toUpperCase()} START</text>
          </g>
        );
      })}
      {/* Daily readings: faint. */}
      {props.raw.map((p, i) => p.value === null ? null : (
        <circle key={p.day} cx={x(i)} cy={y(p.value)} r="1.6" fill="var(--cream-dim)" opacity="0.45" />
      ))}
      {/* The rolling mean: the line to read. */}
      <path d={pathFor(props.smooth, x, y)} fill="none" stroke="var(--coral)" stroke-width="1.6" stroke-linejoin="round" />
      {/* Dose ticks along the bottom, in the same graduated language. */}
      <line x1={padL} x2={W - padR} y1={H - padB + 6} y2={H - padB + 6} stroke="var(--line-lit)" stroke-width="1" />
      {props.marks.map((m, k) => {
        const i = idx.get(m.day);
        if (i === undefined) return null;
        return <rect key={k} x={x(i) - 1} y={H - padB + 2} width="2" height="9" fill={compColor.get(m.compoundId) ?? 'var(--sage)'} />;
      })}
      <text x={padL} y={H - 4} fill="var(--muted)" font-family="var(--mono)" font-size="8">{short(first)}</text>
      <text x={W - padR} y={H - 4} text-anchor="end" fill="var(--muted)" font-family="var(--mono)" font-size="8">{short(last)}</text>
    </svg>
  );
}
